import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  payrollRegistry,
  type AccrualRegistryItem,
  type EmployeeDebtItem,
} from '@/api/payroll_registry';
import { orgs } from '@/api/catalogs';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

const fmt2 = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const MONTHS = ['', 'Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень',
  'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень'];

const KIND_LABEL: Record<string, string> = {
  MAIN: 'Основна', ADVANCE: 'Аванс', VACATION: 'Відпускні',
  SICK: 'Лікарняні', COMPENSATION: 'Компенсація', CIVIL: 'ЦПУ',
};

type Mode = 'people' | 'docs';

export default function EmployeeDebtsPage() {
  const navigate = useNavigate();
  const [orgId, setOrgId] = useState('');
  const [mode, setMode] = useState<Mode>('people');

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const list = useQuery({
    queryKey: ['employee-debts', orgId],
    queryFn: () => payrollRegistry.employeeDebts(orgId),
    enabled: !!orgId && mode === 'people',
  });
  // По-документно: реєстр нарахувань уже рахує per-документ paid/debt —
  // фільтруємо проведені з боргом > 0 (без період-фільтра: всі періоди).
  const docsQ = useQuery({
    queryKey: ['document-debts', orgId],
    queryFn: () => payrollRegistry.accruals({ organization_id: orgId }),
    enabled: !!orgId && mode === 'docs',
  });
  const debtDocs = useMemo(
    () => (docsQ.data ?? []).filter((r) => r.posted && r.debt != null && Number(r.debt) > 0),
    [docsQ.data]);

  const total = useMemo(() => mode === 'people'
    ? (list.data ?? []).reduce((s, r) => s + Number(r.amount), 0)
    : debtDocs.reduce((s, r) => s + Number(r.debt), 0),
  [mode, list.data, debtDocs]);

  // «оплатити» → нова відомість на виплату, предзаповнена з документа боргу
  // (org + вид + період + документ; редактор сам підтягне рядки з залишками).
  function payDoc(r: AccrualRegistryItem, payoutKind: string) {
    const docId = r.route === 'sick' ? (r.calculation_id ?? r.id) : r.id;
    navigate(`/payroll-payments/new?org=${r.organization_id}&kind=${payoutKind}`
      + `&year=${r.period_year}&month=${r.period_month}&doc=${docId}`);
  }

  const peopleCols = useMemo<ColumnDef<EmployeeDebtItem>[]>(() => [
    { key: 'employee', header: 'Працівник', width: 220,
      sortValue: (r) => r.employee_name, cell: (r) => r.employee_name },
    { key: 'period', header: 'Період', width: 140,
      sortValue: (r) => r.period_year * 100 + r.period_month,
      cell: (r) => `${MONTHS[r.period_month]} ${r.period_year}` },
    { key: 'kind', header: 'Вид', width: 200,
      sortValue: (r) => r.kind_label, cell: (r) => r.kind_label },
    { key: 'amount', header: 'Борг', width: 130, align: 'right',
      sortValue: (r) => Number(r.amount), cell: (r) => fmt2(r.amount) },
  ], []);

  const docCols = useMemo<ColumnDef<AccrualRegistryItem>[]>(() => [
    { key: 'document_number', header: '№', width: 120, importBadge: true,
      sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
    { key: 'kind', header: 'Вид', width: 120,
      sortValue: (r) => r.kind, cell: (r) => KIND_LABEL[r.kind] ?? r.kind },
    { key: 'period', header: 'Період', width: 130,
      sortValue: (r) => (r.period_year ?? 0) * 100 + (r.period_month ?? 0),
      cell: (r) => r.period_month ? `${MONTHS[r.period_month]} ${r.period_year}` : '—' },
    { key: 'employee', header: 'Працівник', width: 190,
      sortValue: (r) => r.employee_name ?? '',
      cell: (r) => r.employee_name ?? '(усі)' },
    { key: 'net', header: 'До виплати', width: 110, align: 'right',
      sortValue: (r) => Number(r.net), cell: (r) => fmt2(r.net) },
    { key: 'paid', header: 'Виплачено', width: 110, align: 'right',
      sortValue: (r) => Number(r.paid ?? 0), cell: (r) => fmt2(r.paid ?? 0) },
    { key: 'debt', header: 'Борг', width: 110, align: 'right',
      sortValue: (r) => Number(r.debt ?? 0),
      cell: (r) => <span style={{ color: '#b91c1c', fontWeight: 600 }}>{fmt2(r.debt ?? 0)}</span> },
    { key: 'pay', header: '', width: 190, align: 'center',
      cell: (r) => r.kind === 'SICK'
        ? (
          <span style={{ display: 'inline-flex', gap: 4 }}>
            <button onClick={(e) => { e.stopPropagation(); payDoc(r, 'SICK_ORG'); }}>
              оплатити (орг)
            </button>
            <button onClick={(e) => { e.stopPropagation(); payDoc(r, 'SICK_PFU'); }}>
              оплатити (ПФУ)
            </button>
          </span>
        )
        : (
          <button onClick={(e) => { e.stopPropagation(); payDoc(r, r.kind); }}>
            оплатити
          </button>
        ) },
  ], []);

  return (
    <div className="card">
      <div className="toolbar" style={{ gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <h2 style={{ margin: 0 }}>Борги за нарахуваннями</h2>
        <div style={{ display: 'flex', border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
          {([['people', 'По працівниках'], ['docs', 'По документах']] as const).map(([m, l]) => (
            <button key={m} onClick={() => setMode(m)}
              style={{
                padding: '0.3rem 0.7rem', border: 'none', cursor: 'pointer', fontSize: '0.8rem',
                background: mode === m ? 'var(--primary)' : 'transparent',
                color: mode === m ? '#fff' : 'var(--text-secondary)',
              }}>
              {l}
            </button>
          ))}
        </div>
      </div>
      <p style={{ color: 'var(--muted)', fontSize: '0.85rem', marginTop: 0 }}>
        Нараховане, але ще не виплачене працівникам. «По працівниках» — з реєстру
        взаєморозрахунків (період × вид рахунку); «По документах» — залишок боргу
        по кожному документу-нарахуванню, звідти ж створюється виплата.
      </p>

      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        <div className="field" style={{ minWidth: 240, margin: 0 }}>
          <label>Організація *</label>
          <select value={orgId} onChange={(e) => setOrgId(e.target.value)}>
            <option value="">— оберіть —</option>
            {orgList.data?.items.map((o) => <option key={o.id} value={o.id}>{o.code} — {o.name}</option>)}
          </select>
        </div>
        {orgId && (
          <div style={{ marginLeft: 'auto', fontWeight: 600 }}>
            Разом боргу: {fmt2(total)} грн
          </div>
        )}
      </div>

      {!orgId ? (
        <p style={{ color: 'var(--muted)' }}>Оберіть організацію.</p>
      ) : mode === 'people' ? (
        <DataTable<EmployeeDebtItem>
          rows={list.data ?? []}
          columns={peopleCols}
          rowKey={(r) => `${r.employee_id}-${r.period_year}-${r.period_month}-${r.account_code}`}
          loading={list.isLoading}
          emptyText="Боргів немає — усе нараховане виплачено."
          initialSortKey="employee"
          initialSortDir="asc"
        />
      ) : (
        <DataTable<AccrualRegistryItem>
          rows={debtDocs}
          columns={docCols}
          rowKey={(r) => r.id}
          loading={docsQ.isLoading}
          emptyText="Боргів немає — усе нараховане виплачено."
          initialSortKey="period"
          initialSortDir="desc"
        />
      )}
    </div>
  );
}
